import React, { useEffect, useState } from 'react';
import { Button, Table, Tag, Modal, message, Space, Tooltip } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  UndoOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useUserAssets, useAssetActions, useAssets } from '../../../../../hooks/useAssets';
import { Asset, AssetStatus } from '../../../../../types/asset.types';
import { User } from '../../../../../types/user.types';
import CreateAssetModal from '../../../assets/components/CreateAssetModal';
import AssignAssetModal from '../../../assets/components/AssignAssetModal';
import EditAssetModal from '../../../assets/components/EditAssetModal';
import UpdateAssetModal from '../../../assets/components/UpdateAssetModal';

const { confirm } = Modal;

interface EmployeeAssetsProps {
  userData: User;
}

const EmployeeAssets: React.FC<EmployeeAssetsProps> = ({ userData }) => {
  const { userAssets, loading, fetchUserAssets } = useUserAssets(userData.id);
  const { deleteAsset, returnAsset, assignAsset } = useAssetActions();
  const { assets, employees, fetchAssets, fetchEmployees } = useAssets();

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const isResigned = userData?.status?.toLowerCase() === "resigned"

  useEffect(() => {
    fetchUserAssets();
    fetchAssets();
    fetchEmployees();
  }, [fetchUserAssets, fetchAssets, fetchEmployees]);

  const handleDelete = async (assetId: number) => {
    try {
      await deleteAsset(assetId);
      fetchUserAssets(); // Listeyi yenile
    } catch (error) {
      // console.error('Delete failed:', error);
    }
  };

  const showDeleteConfirm = (asset: Asset) => {
    confirm({
      title: 'Zimmeti silmek istediğinize emin misiniz?',
      icon: <ExclamationCircleOutlined />,
      content: `${asset.name} (${asset.serialNumber}) kalıcı olarak silinecek.`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk() {
        handleDelete(asset.id);
      },
    });
  };

  const handleEditAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setEditModalVisible(true);
  };

  const handleStatusUpdate = (asset: Asset) => {
    setSelectedAsset(asset);
    setUpdateModalVisible(true);
  };

  const handleReturnAsset = async (asset: Asset) => {
    try {
      await returnAsset(asset.id);
      fetchUserAssets();
    } catch (error) {
      // console.error('Return asset failed:', error);
    }
  };

  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'success';
      case AssetStatus.ASSIGNED:
        return 'processing';
      case AssetStatus.SCRAPPED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'Envanterde';
      case AssetStatus.ASSIGNED:
        return 'Atanmış';
      case AssetStatus.SCRAPPED:
        return 'Kullanım Dışı';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'Zimmet Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Asset, b: Asset) => a.name.localeCompare(b.name),
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      sorter: (a: Asset, b: Asset) => a.serialNumber.localeCompare(b.serialNumber),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      sorter: (a: Asset, b: Asset) => a.status.localeCompare(b.status),
    },
    {
      title: 'Zimmet Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
      sorter: (a: Asset, b: Asset) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Dosyalar',
      dataIndex: 'files',
      key: 'files',
      render: (files: any[]) => (
        <div>
          {files && files.length > 0 ? (
            <Tooltip title={`${files.length} dosya mevcut`}>
              <Button type="text" size="small" icon={<FileTextOutlined />}>
                {files.length}
              </Button>
            </Tooltip>
          ) : (
            <span className="text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (text: any, record: Asset) => (
        <Space>
          <Tooltip title="Bilgileri Düzenle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditAsset(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          {record.status === AssetStatus.ASSIGNED && (
            <Tooltip title="İade Al">
              <Button
                type="text"
                icon={<UndoOutlined />}
                onClick={() => handleReturnAsset(record)}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
          )}
          <Tooltip title="Durum Güncelle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleStatusUpdate(record)}
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Sil">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record)}
              danger
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    fetchUserAssets();
    message.success('Zimmet başarıyla oluşturuldu');
  };

  const handleAssignSuccess = () => {
    setAssignModalVisible(false);
    setSelectedAsset(null);
    fetchUserAssets();
    fetchAssets(); // Ana zimmet listesini de güncelle
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedAsset(null);
    fetchUserAssets();
  };

  const handleUpdateSuccess = () => {
    setUpdateModalVisible(false);
    setSelectedAsset(null);
    fetchUserAssets();
    message.success('Zimmet durumu güncellendi');
  };

  // İstatistikler
  const totalAssets = userAssets?.length || 0;
  const assignedAssets = userAssets?.filter(asset => asset.status === AssetStatus.ASSIGNED).length || 0;
  const returnedAssets = userAssets?.filter(asset => asset.status === AssetStatus.IN_STOCK).length || 0;
  const scrappedAssets = userAssets?.filter(asset => asset.status === AssetStatus.SCRAPPED).length || 0;

  // Envanterdeki (atanmamış) zimmetler
  const availableAssets = assets?.filter(asset => asset.status === AssetStatus.IN_STOCK) || [];

  return (
    <div className="accordion-item">
      <div className="accordion-header" id="headingAssets">
        <div className="accordion-button" role="button" data-bs-toggle="collapse" data-bs-target="#collapseAssets" style={{ cursor: 'pointer' }}>
          <div className="d-flex align-items-center justify-content-between flex-fill">
            {/* <i className="ti ti-archive me-2"></i> */}
            <h5 className="mb-0">Zimmet Yönetimi</h5>
            <div className="d-flex">
              <i className="ti ti-chevron-down fs-18" />
            </div>
          </div>
        </div>
      </div>
      <div id="collapseAssets" className="accordion-collapse collapse" data-bs-parent="#accordionExample">
        <div className="accordion-body">
          {/* İstatistikler */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-primary me-2">
                      <i className="ti ti-archive text-white"></i>
                    </span>
                    <h4 className="mb-0">{totalAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">Toplam</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-warning me-2">
                      <i className="ti ti-user-check text-white"></i>
                    </span>
                    <h4 className="mb-0">{assignedAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">Atanmış</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-success me-2">
                      <i className="ti ti-package text-white"></i>
                    </span>
                    <h4 className="mb-0">{returnedAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">Envanterde</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-danger me-2">
                      <i className="ti ti-trash text-white"></i>
                    </span>
                    <h4 className="mb-0">{scrappedAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">Kullanım Dışı</p>
                </div>
              </div>
            </div>
          </div>

          {/* Zimmet İşlemleri Butonları */}
          <div className="d-flex justify-content-end gap-2 mb-3">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAssignModalVisible(true)}
              disabled={
                availableAssets.length === 0 || isResigned
              }
            >
              Zimmet Ata ({availableAssets.length} mevcut)
            </Button>
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Yeni Zimmet Oluştur
            </Button>
          </div>

          {/* Zimmet Tablosu */}
          <Table
            columns={columns}
            dataSource={userAssets || []}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 5,
              size: 'small',
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
            }}
            size="small"
          />

          {/* Modals */}
          <CreateAssetModal
            visible={createModalVisible}
            onClose={() => setCreateModalVisible(false)}
            onSuccess={handleCreateSuccess}
          />

          {!isResigned && <AssignAssetModal
            visible={assignModalVisible}
            onClose={() => {
              setAssignModalVisible(false);
              setSelectedAsset(null);
            }}
            onSuccess={handleAssignSuccess}
            asset={null} // Atama modalında zimmet seçilecek
            employees={[userData]} // Sadece mevcut kullanıcı
            availableAssets={availableAssets} // Envanterdeki zimmetler
          />}

          <EditAssetModal
            visible={editModalVisible}
            onClose={() => {
              setEditModalVisible(false);
              setSelectedAsset(null);
            }}
            onSuccess={handleEditSuccess}
            asset={selectedAsset}
          />

          <UpdateAssetModal
            visible={updateModalVisible}
            onClose={() => {
              setUpdateModalVisible(false);
              setSelectedAsset(null);
            }}
            onSuccess={handleUpdateSuccess}
            asset={selectedAsset}
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeAssets; 